import { timeUnitOf, truncSql, timeKeySql, bucketStartSql } from '@rdrive/plugin-chart-time-series';
import { queryRows } from '@rdrive/data-duckdb';
import { makeBandTitles } from './issueStatusBands';

// Status-change velocity ("how many issues changed into each status colour per period") for
// @rdrive/plugin-chart-time-series' BaseTimeSeriesChart (mode="periodic", seriesType="bar") —
// IA_ABSORPTION_PLAN Phase 4 step 12c; was components/charts/StatusHistoryVelocityLineChart.vue
// (a stacked BAR chart despite its name), whose series query this is, VERBATIM (the bucket
// expressions now come from the package's `timeBuckets` helpers, which print the same text, except
// that week keys carry the ISO year — `%G-W%V`, see useIssueBurndownChart.js). The
// tooltip's bucket start is `bucketStartSql`'s — the ISO (Jan 4) week anchor, which fixes the
// old Jan-1-based parsing that landed a week early whenever Jan 1 fell on a Friday–Sunday (the
// same fix project-overview's velocity got at S11). The series objects, colours, tooltip markup,
// zoom and PNG export are the base's.
//
// The page supplies the filter contexts — BOTH (the issues and the status-history context): the
// SQL reads `filteredStatusHistory` joined to `filter_context`, and the old SFC reloaded on a
// tick from either. This config has none of its own, so it uses plain `queryRows`.
//
// Returns { fetchSeries, fetchTooltip, fetchLegendTitles }, spread onto the base.
export function useIssueVelocityChart({ conn }) {
  const run = (query) => queryRows(conn, query);
  // The PNG legend's names: the status titles each colour band carried in the last series load
  const bandTitles = makeBandTitles();

  const generateActivityQuery = (groupBy) => {
    const timeUnit = timeUnitOf(groupBy);
    const dateTruncSql = truncSql(timeUnit, 'validFrom');
    const bucketKeySql = timeKeySql(timeUnit, dateTruncSql, { isoYear: true });

    return `
WITH time_periods AS (
  SELECT DISTINCT
    ${dateTruncSql} AS period_start,
    ${bucketKeySql} AS time_key
  FROM filteredStatusHistory sht
  JOIN filter_context fc ON sht.subItemId = fc.Id
  WHERE validFrom IS NOT NULL
),
status_activity AS (
  SELECT
    tp.time_key,
    sht.statusId,
    st.color,
    st.title,
    st.sort,
    st.isDraft,
    COUNT(DISTINCT sht.subItemId) AS activity_count
  FROM time_periods tp
  JOIN filteredStatusHistory sht
    ON ${truncSql(timeUnit, 'sht.validFrom')} = tp.period_start
  JOIN filter_context fc ON sht.subItemId = fc.Id
  LEFT JOIN status_templates st ON sht.statusId = st.id
  GROUP BY
    tp.time_key,
    sht.statusId,
    st.color,
    st.title,
    st.sort,
    st.isDraft
  HAVING COUNT(DISTINCT sht.subItemId) > 0
),
color_activity AS (
  SELECT
    time_key,
    color,
    MIN(sort) AS sort,
    STRING_AGG(DISTINCT title, ', ') AS titles,
    SUM(activity_count) AS count
  FROM status_activity
  GROUP BY time_key, color
),
all_time_periods AS (
  SELECT time_key
  FROM time_periods
),
all_colors AS (
  SELECT DISTINCT color, MIN(sort) AS sort
  FROM status_templates
  GROUP BY color
),
all_periods_and_colors AS (
  SELECT tp.time_key, c.color, c.sort
  FROM all_time_periods tp
  CROSS JOIN all_colors c
)
SELECT
  apc.time_key as time_key,
  apc.color as color,
  apc.sort as sort,
  COALESCE(ca.titles, '') AS titles,
  COALESCE(ca.count, 0) AS count
FROM all_periods_and_colors apc
LEFT JOIN color_activity ca
  ON apc.time_key = ca.time_key AND apc.color = ca.color
ORDER BY apc.time_key, apc.sort
`;
  };

  // Query for detailed tooltip data: one bucket's status changes, grouped by title + colour.
  // `bucketStartSql` THROWS on a malformed key; the base catches per fetch and renders an empty
  // breakdown (the old SFC logged "Error parsing timePoint" and ran an empty query instead).
  const generateTooltipQuery = (timePoint, groupBy) => {
    const timeUnit = timeUnitOf(groupBy);
    const boundaryTimestampSQL = bucketStartSql(timeUnit, timePoint);

    // Query to show activity details for the selected time period, grouped by title + color
    return `
WITH specific_time_period AS (
  -- Define the single time period we care about
  SELECT ${boundaryTimestampSQL} AS period_start
),
status_counts AS (
  -- Count statuses with consistent date truncation
  SELECT
    sht.statusId,
    COUNT(DISTINCT sht.subItemId) AS activity_count
  FROM specific_time_period stp
  JOIN filteredStatusHistory sht
    ON ${truncSql(timeUnit, 'sht.validFrom')} = stp.period_start
  JOIN filter_context fc ON sht.subItemId = fc.Id
  GROUP BY
    sht.statusId
),
title_color_groups AS (
  -- Join with status templates and group by title/color
  SELECT
    st.title,
    st.color,
    st.isDraft,
    MIN(st.sort) AS sort_order,
    SUM(sc.activity_count) AS activity_count
  FROM status_counts sc
  JOIN status_templates st ON sc.statusId = st.id
  GROUP BY
    st.title,
    st.color,
    st.isDraft
  HAVING SUM(sc.activity_count) > 0
)
-- Return the grouped data
SELECT
  title as title,
  color as color,
  sort_order AS sort,
  activity_count AS count
FROM title_color_groups
ORDER BY
  sort_order,
  title;
`;
  };

  // Rows: { timeKey, color (may be null), value, sort } — `value` is the per-bucket count; each
  // row's `titles` feed the PNG legend's band names.
  const fetchSeries = async (groupBy) => {
    const rows = await run(generateActivityQuery(groupBy));
    bandTitles.collect(rows);
    return rows.map((row) => ({
      timeKey: row.time_key,
      color: row.color,
      value: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Rows: { title, color, sort, count } for one time bucket
  const fetchTooltip = async (timePoint, groupBy) => {
    const rows = await run(generateTooltipQuery(timePoint, groupBy));
    return rows.map((row) => ({
      ...row,
      count: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  return { fetchSeries, fetchTooltip, fetchLegendTitles: bandTitles.fetchLegendTitles };
}
